import {
  PlatePosition,
  ReactSelectableFast,
  SelectionProcess,
  SelectionType,
  WorklistValues,
} from "features/WorklistTools/shared/interfaces";
import {
  applyRandomizeWells,
  arrangeColWise,
  arrangeRowWise,
  checkForDoubleSelectionWithMultipleDestSelection,
  getWellSelectionType,
  randomizeSelectedWells,
  stampSourceToDestination,
} from "features/WorklistTools/shared/WorklistHelpers";
import { Alert, AlertType } from "shared-components/toast";
import { WorklistValuesState } from "../../state";
import { IExternalThawInternalState } from "../../state/initial-state";
import { addDestinationWellMapping } from "./addDestinationWellMapping";

export const selectDestWells = async (
  externalThawInternalState: IExternalThawInternalState,
  intPlateInfo: any,
  destPlateInfo: any,
  worklistValues: WorklistValuesState,
  dispatch: (action: { type: string; payload: any }) => void,
  selectedWells: ReactSelectableFast[],
  isConfirmed: (
    prompt: string,
    bullets?: string[] | undefined
  ) => Promise<boolean>
) => {
  const destSelection = selectedWells.map((well) => ({
    plateIndex: well.props.plateIndex,
    plateWellId: well.props.plateWellId,
    isSelectable: false,
  }));

  if (destSelection.length === 0) return;

  if (externalThawInternalState.selectedIntToDestWells.length === 0) {
    Alert({
      type: AlertType.ERROR,
      message: "You must select an Intermediate well",
    });
    return;
  }

  let randomizedWells = [...externalThawInternalState.selectedIntToDestWells];
  if (externalThawInternalState.randomizeWells) {
    randomizedWells = await randomizeSelectedWells(randomizedWells);
  }
  const selectionType = getWellSelectionType(
    externalThawInternalState.selectedIntToDestWells,
    destSelection
  );
  const sourceIntIndex =
    externalThawInternalState.selectedIntToDestWells[0].plateIndex;
  let wellMapping: WorklistValues[] = [];

  switch (selectionType) {
    case SelectionType.NoSourceWellSelected:
      Alert({
        type: AlertType.ERROR,
        message: "Please select a source well(s)",
      });
      break;
    case SelectionType.SelectMultipleSourceAndDestWells:
      Alert({
        type: AlertType.ERROR,
        message: "You can only select 1 Destination Well",
      });
      break;
    case SelectionType.SelectMultipleSourceAndOneDestWell:
      switch (externalThawInternalState.destSelectionProcess) {
        case SelectionProcess.Stamp:
          wellMapping = stampSourceToDestination(
            worklistValues.int1ToDest,
            externalThawInternalState.selectedIntToDestWells,
            destSelection[0],
            PlatePosition.Destination,
            false //no pooling
          );
          if (wellMapping.length === 0) {
            Alert({
              type: AlertType.ERROR,
              message: "Selection does not fit in Dest Plate.",
            });
            return;
          }

          addDestinationWellMapping(
            dispatch,
            sourceIntIndex,
            externalThawInternalState.randomizeWells
              ? applyRandomizeWells(wellMapping, randomizedWells)
              : wellMapping,
            worklistValues
          );
          break;
        case SelectionProcess.RowWise:
          wellMapping = await arrangeRowWise(
            worklistValues.int1ToDest,
            externalThawInternalState.selectedIntToDestWells,
            destSelection[0],
            destPlateInfo[0].rows,
            destPlateInfo[0].cols,
            PlatePosition.Destination,
            false //no pooling
          );
          addDestinationWellMapping(
            dispatch,
            sourceIntIndex,
            externalThawInternalState.randomizeWells
              ? applyRandomizeWells(wellMapping, randomizedWells)
              : wellMapping,
            worklistValues
          );
          break;
        case SelectionProcess.ColWise:
          wellMapping = await arrangeColWise(
            worklistValues.int1ToDest,
            externalThawInternalState.selectedIntToDestWells,
            destSelection[0],
            destPlateInfo[0].rows,
            destPlateInfo[0].cols,
            PlatePosition.Destination,
            false //no pooling
          );

          addDestinationWellMapping(
            dispatch,
            sourceIntIndex,
            externalThawInternalState.randomizeWells
              ? applyRandomizeWells(wellMapping, randomizedWells)
              : wellMapping,
            worklistValues
          );
          break;
      }
      break;
    case SelectionType.SelectOneSourceAndOneDestWell:
      {
        const error = checkForDoubleSelectionWithMultipleDestSelection(
          worklistValues.int1ToDest,
          [
            {
              plateIndex: destSelection[0].plateIndex,
              plateWellId: destSelection[0].plateWellId,
              isSelectable: true,
            },
          ]
        );
        if (error) {
          Alert({
            type: AlertType.ERROR,
            message: "Well has already been selected",
          });
          break;
        }
        if (externalThawInternalState.selectedIntToDestWells[0].isSelectable) {
          addDestinationWellMapping(
            dispatch,
            sourceIntIndex,
            [
              {
                sourcePlateIndex: sourceIntIndex,
                sourceWellId:
                  externalThawInternalState.selectedIntToDestWells[0]
                    .plateWellId,
                destPlateIndex: destSelection[0].plateIndex,
                destWellId: destSelection[0].plateWellId,
                transferVol: "",
              },
            ],
            worklistValues
          );
        }
      }
      break;
    case SelectionType.SelectOneSourceAndMultipleDestWells: {
      const error = checkForDoubleSelectionWithMultipleDestSelection(
        worklistValues.int1ToDest,
        [
          {
            plateIndex: destSelection[0].plateIndex,
            plateWellId: destSelection[0].plateWellId,
            isSelectable: true,
          },
        ]
      );
      if (error) {
        Alert({
          type: AlertType.ERROR,
          message: "Well has already been selected",
        });
        break;
      }
      for (const row of destSelection) {
        if (externalThawInternalState.selectedIntToDestWells[0].isSelectable) {
          wellMapping.push({
            sourcePlateIndex: sourceIntIndex,
            sourceWellId:
              externalThawInternalState.selectedIntToDestWells[0].plateWellId,
            destPlateIndex: row.plateIndex,
            destWellId: row.plateWellId,
            transferVol: "",
          });
        }
      }
      if (wellMapping.length > 0) {
        addDestinationWellMapping(
          dispatch,
          sourceIntIndex,
          wellMapping,
          worklistValues
        );
      }
      break;
    }
  }
};
